import http from '@/services/http'
import delayRequest from '@/utils/delay-request'
import * as types from './startup-profile.types'
import { COUNTRIES_CHOICES } from '@/services/countries'
import { normalizeLink } from '@/utils'
import {
  DEFAULT_PITCH_SECTIONS,
  DEFAULT_FAQ_SECTIONS,
} from '@/services/startup'
import router from '@/routes'
import { pick } from 'lodash'
import { getEmptyStartup } from './startup-model'

export default {
  namespaced: true,
  state: {
    fetchInProgress: false,
    fetchDone: false,
    saveInProgress: false,
    updateMembersInProgress: false,
    startup: getEmptyStartup(),
    errors: {},
    countriesChoices: COUNTRIES_CHOICES,
    links: [],
    linksFetchInProgress: false,
    linksSaveInProgress: false,
    linksRemoveInProgress: false,
    rateInProgress: false,
  },
  getters: {
    websiteLink: (state) => normalizeLink(state.startup.website),
    facebookLink: (state) => normalizeLink(state.startup.facebook),
    twitterLink: (state) => normalizeLink(state.startup.twitter),
    linkedinLink: (state) => normalizeLink(state.startup.linkedin),
    publicLink: (state) =>
      router.resolve({
        name: 'publicStartupProfile',
        params: { slug: state.startup.slug || '-' },
      }).href,
  },
  mutations: {
    [types.API_ERROR](state, errors) {
      state.errors = errors
    },
    CLEAN_API_ERRORS(state) {
      state.errors = {}
    },
    [types.START_FETCH](state) {
      state.fetchInProgress = true
    },
    [types.SET_STARTUP_DATA](state, startupData) {
      state.fetchDone = true
      if (startupData.pitch && startupData.pitch.length === 0) {
        Object.assign(startupData, { pitch: DEFAULT_PITCH_SECTIONS })
      }
      // TODO: remove this once we migrate initial faq to backend (#809)
      if (startupData.faq && startupData.faq.length === 0) {
        Object.assign(startupData, { faq: DEFAULT_FAQ_SECTIONS })
      }
      Object.assign(state.startup, startupData)
      state.startup = startupData
    },
    [types.SET_STARTUP_ATTR](state, { attr, value }) {
      Object.assign(state.startup, { [attr]: value })
      // TODO: this is how it should be made in order to broadcast changes automatically
      // state.startup = Object.assign({}, state.startup, {[attr]: value})
    },
    [types.STOP_FETCH](state) {
      state.fetchInProgress = false
    },
    [types.START_FOLLOWING](state) {
      state.startup.follows = true
    },
    [types.STOP_FOLLOWING](state) {
      state.startup.follows = false
    },
    [types.START_SAVE](state) {
      state.saveInProgress = true
    },
    [types.STOP_SAVE](state) {
      state.saveInProgress = false
    },
    [types.SHOW_PITCH_SECTION](state, { section }) {
      state.startup.pitch = state.startup.pitch.map((s) => {
        if (s === section) {
          s.hidden = false
        }
        return s
      })
    },
    SET_PITCH_SECTION(state, { idx, section }) {
      state.startup.pitch = state.startup.pitch.map((s, i) => {
        if (idx === i) {
          return section
        } else {
          return s
        }
      })
    },
    ADD_TRAILING_PARAGRAPH_TO_SECTION(state, { idx }) {
      state.startup.pitch = state.startup.pitch.map((s, i) => {
        if (idx === i) {
          const newSection = Object.assign({}, s)
          newSection.content += '<p><br></p>'
          return newSection
        } else {
          return s
        }
      })
    },
    PITCH_SECTION_MOVED_BACK(state, { sectionIdx }) {
      if (sectionIdx >= 1 && state.startup.pitch.length >= 2) {
        state.startup.pitch = swap(
          state.startup.pitch,
          sectionIdx,
          sectionIdx - 1,
        )
      } else {
        throw new Error(`Section [${sectionIdx}] cannot be moved back`)
      }
    },
    PITCH_SECTION_MOVED_FORWARD(state, { sectionIdx }) {
      if (state.startup.pitch.length > sectionIdx) {
        state.startup.pitch = swap(
          state.startup.pitch,
          sectionIdx,
          sectionIdx + 1,
        )
      } else {
        throw new Error(`Section [${sectionIdx}] cannot be moved forward`)
      }
    },
    [types.REMOVE_PITCH_SECTION](state, { section }) {
      state.startup.pitch = state.startup.pitch.filter((s) => section !== s)
    },
    RESTORE_PITCH_SECTION(state, { section, index }) {
      state.startup.pitch.splice(index, 0, section)
    },
    [types.PUBLISH](state) {
      state.startup.is_published = true
    },
    [types.CONCEAL](state) {
      state.startup.is_published = false
    },
    ADD_DOCUMENT(state, document) {
      state.startup.documents = state.startup.documents.concat([document])
    },
    REMOVE_DOCUMENT(state, { document }) {
      state.startup.documents = state.startup.documents.filter(
        (d) => d.id !== document.id,
      )
    },
    SET_CURRENT_ROUND_TYPE(state, type) {
      state.startup.round_type = type
    },
    SET_CURRENT_ROUND_SECURITIES(state, securities) {
      state.startup.round_securities = securities
    },
    SET_CURRENT_ROUND_SOURCES(state, sources) {
      state.startup.round_sources = sources
    },
    SET_PREVIOUS_ROUND_SOURCES(state, { round, sources }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].sources = sources
    },
    SET_PREVIOUS_ROUND_SECURITIES(state, { round, securities }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].securities = securities
    },
    SET_PREVIOUS_ROUND_TYPE(state, { round, type }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].type = type
    },
    SET_PREVIOUS_ROUND_SIZE(state, { round, size }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].size = size
    },
    SET_PREVIOUS_ROUND_YEAR(state, { round, year }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].year = year
    },
    SET_PREVIOUS_ROUND_INVESTOR(state, { round, investor }) {
      const roundIdx = getRoundIdx(state, round)
      state.startup.previous_rounds[roundIdx].investor = investor
    },
    REMOVE_PREVIOUS_ROUND(state, roundIdx) {
      if (roundIdx > -1) {
        state.startup.previous_rounds.splice(roundIdx, 1)
      }
    },
    REMOVE_CURRENT_ROUND(state) {
      state.startup.round_size = null
      state.startup.round_type = null
      state.startup.round_securities = []
      state.startup.round_sources = []
    },
    ADD_MEMBER_SLOT(state) {
      state.startup.members.push({
        name: null,
        title: null,
        bio: null,
        image_url: null,
        facebook_url: null,
        twitter_url: null,
        linkedin_url: null,
      })
    },
    REMOVE_MEMBER(state, index) {
      state.startup.members.splice(index, 1)
    },
    START_UPDATE_MEMBERS(state) {
      state.updateMembersInProgress = true
    },
    STOP_UPDATE_MEMBERS(state) {
      state.updateMembersInProgress = false
    },
    UPDATE_MEMBER_IMAGE_URL(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.image_url = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_TWITTER_URL(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.twitter_url = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_FACEBOOK_URL(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.facebook_url = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_LINKEDIN_URL(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.linkedin_url = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_NAME(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.name = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_TITLE(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.title = value
      state.startup.members.splice(index, 1, obj)
    },
    UPDATE_MEMBER_BIO(state, { value, index }) {
      const obj = state.startup.members[index]

      obj.bio = value
      state.startup.members.splice(index, 1, obj)
    },

    ADD_LINK(state, { id, name }) {
      state.links = state.links.concat([{ id, name }])
    },
    REMOVE_LINK(state, { id }) {
      state.links = state.links.filter((link) => link.id !== id)
    },
    RESTORE_LINK(state, { id, name }) {
      state.links = state.links.concat([{ id, name }])
    },
    START_LINKS_FETCH(state) {
      state.linksFetchInProgress = true
    },
    STOP_LINKS_FETCH(state) {
      state.linksFetchInProgress = false
    },
    SET_LINKS(state, { links }) {
      state.links = links
    },
    UPDATE_BACKGROUND_IMAGE(state, value) {
      state.startup.bg_img_url = value
    },
    ADD_FAQ_SECTION(state) {
      state.startup.faq.push({
        section: '',
        items: [
          {
            question: '',
            answer: '',
          },
        ],
      })
    },
    REMOVE_FAQ_SECTION(state, { section }) {
      state.startup.faq = state.startup.faq.filter((s) => section !== s)
    },
    RESTORE_FAQ_SECTION(state, { section, index }) {
      state.startup.faq.splice(index, 0, section)
    },
    FAQ_SECTION_MOVED_BACK(state, { index }) {
      if (index >= 1 && state.startup.faq.length >= 2) {
        state.startup.faq = swap(state.startup.faq, index, index - 1)
      } else {
        throw new Error(`Section [${index}] cannot be moved back`)
      }
    },
    FAQ_SECTION_MOVED_FORWARD(state, { index }) {
      if (state.startup.faq.length > index) {
        state.startup.faq = swap(state.startup.faq, index, index + 1)
      } else {
        throw new Error(`Section [${index}] cannot be moved forward`)
      }
    },
    UPDATE_FAQ_SECTION(state, { value, index }) {
      const obj = state.startup.faq[index]

      obj.section = value
      state.startup.faq.splice(index, 1, obj)
    },
    ADD_FAQ_ELEMENT(state, { index }) {
      state.startup.faq[index].items.push({ question: '', answer: '' })
    },
    UPDATE_FAQ_QUESTION(state, { value, elementId, sectionId }) {
      const obj = state.startup.faq[sectionId].items[elementId]

      obj.question = value
      state.startup.faq[sectionId].items.splice(elementId, 1, obj)
    },
    UPDATE_FAQ_ANSWER(state, { value, elementId, sectionId }) {
      const obj = state.startup.faq[sectionId].items[elementId]

      obj.answer = value
      state.startup.faq[sectionId].items.splice(elementId, 1, obj)
    },
    REMOVE_FAQ_ELEMENT(state, { elementId, sectionId }) {
      const items = state.startup.faq[sectionId].items
      const element = items[elementId]
      state.startup.faq[sectionId].items = items.filter((e) => element !== e)
    },

    ENABLE_FEATURE(state, { feature }) {
      state.startup.features[feature] = true
    },
    DISABLE_FEATURE(state, { feature }) {
      state.startup.features[feature] = false
    },

    setRatings(state, ratings) {
      state.startup = { ...state.startup, ratings }
    },
    startRate(state) {
      state.rateInProgress = true
    },
    stopRate(state) {
      state.rateInProgress = false
    },
    clean(state) {
      state.startup = getEmptyStartup()
    },
  },
  actions: {
    clean({ commit }) {
      commit('clean')
    },
    fetch({ commit, state }, opts) {
      const options = opts || {}
      const slug = options.slug
      const id = options.id
      const silent = options.silent
      if (id) {
        commit(types.SET_STARTUP_ATTR, { attr: 'id', value: id })
      }
      if (!silent) {
        commit(types.START_FETCH)
      }
      let url = `/startups/details/${slug}`
      if (!slug && id) {
        url = `/startups/${id}`
      }
      if (!slug && !id && state.startup && state.startup.id) {
        url = `/startups/${state.startup.id}`
      }
      const request = http.getAuthed(url)
      delayRequest(request, 0)
        .then((response) => {
          window.startup = response.data
          commit(types.SET_STARTUP_DATA, response.data)
        })
        .finally(() => {
          if (!silent) {
            commit(types.STOP_FETCH)
          }
        })
    },
    async fetchPublic({ commit }, { slug, isAuthorized, silent = false }) {
      isAuthorized = isAuthorized || false
      const url = `/startups/public-details/${slug}`
      return await fetchWithUrl({ commit, url, isAuthorized, silent })
    },
    async fetchPrivate({ commit }, { startupId, linkId, silent = false }) {
      const url = `/startups/${startupId}/private/${linkId}`
      return await fetchWithUrl({ commit, url, silent })
    },
    startFollowing({ commit }, { startupId }) {
      commit(types.START_FOLLOWING)
      http.postAuthed(`/startups/${startupId}/start-following`)
    },
    stopFollowing({ commit }, { startupId }) {
      commit(types.STOP_FOLLOWING)
      http.postAuthed(`/startups/${startupId}/stop-following`)
    },
    setAttr({ commit }, { attr, value }) {
      commit(types.SET_STARTUP_ATTR, { attr, value })
    },
    async saveChanges({ commit, state }, opts) {
      opts = opts || {}
      const fields = opts.fields || []

      commit(types.START_SAVE)
      const url = `/startups/${state.startup.id}/update`
      let data = Object.assign({}, state.startup)
      if (fields.length) {
        data = pick(data, fields)
      }

      if (data.hasOwnProperty('previous_rounds') && !data.previous_rounds) {
        data.previous_rounds = []
      }

      const nullableFields = [
        'round_size',
        'annual_revenue',
        'monthly_recurring_revenue',
        'employees_count',
      ]
      nullableFields.forEach((field) => {
        if (data[field] === '') {
          data[field] = null
        }
      })

      delete data.documents
      try {
        await http.postAuthed(url, data)
        commit('CLEAN_API_ERRORS')
      } catch (error) {
        commit(types.API_ERROR, error.response.data)
        throw error
      } finally {
        commit(types.STOP_SAVE)
      }
    },
    async reorderDocuments({ commit, state }, { documentsIds }) {
      commit(types.START_SAVE)
      const url = `/startups/${state.startup.id}/reorder-documents`
      try {
        http.postAuthed(url, { documents: documentsIds })
      } finally {
        commit(types.STOP_SAVE)
      }
    },
    addPitchSection({ commit, state }, opts) {
      const options = opts || {}
      const title = options.title || 'Title'
      const content = options.content || '<p>Content</p>'
      const pitch = state.startup.pitch.map((s) => s)
      pitch.push({ title, content })

      commit(types.SET_STARTUP_ATTR, { attr: 'pitch', value: pitch })
    },
    setPitch({ commit }, { pitch }) {
      commit(types.SET_STARTUP_ATTR, { attr: 'pitch', value: pitch })
    },
    hidePitchSection({ commit }, { section }) {
      commit(types.HIDE_PITCH_SECTION, { section })
    },
    removePitchSection({ commit }, { section }) {
      commit(types.REMOVE_PITCH_SECTION, { section })
    },
    showPitchSection({ commit }, { section }) {
      commit(types.SHOW_PITCH_SECTION, { section })
    },

    async publish({ commit, state }) {
      const response = await http.postAuthed(
        `/startups/${state.startup.id}/publish`,
      )
      commit(types.PUBLISH)
      return response
    },
    async conceal({ commit, state }) {
      const response = await http.postAuthed(
        `/startups/${state.startup.id}/conceal`,
      )
      commit(types.CONCEAL)
      return response
    },
    async addDocument({ commit, state }, { document }) {
      commit('ADD_DOCUMENT', { document })
      await http.postAuthed(
        `/startups/${state.startup.id}/add-document`,
        document,
      )
    },
    async removeDocument({ commit, state }, { document }) {
      await http.postAuthed(`/startups/${state.startup.id}/remove-document`, {
        id: document.id,
      })
      commit('REMOVE_DOCUMENT', { document })
    },
    async updateMembers({ commit, state }) {
      commit('START_UPDATE_MEMBERS')
      const { members, id } = state.startup
      const response = await http.postAuthed(
        `/startups/${id}/update-members`,
        members,
      )

      commit('STOP_UPDATE_MEMBERS')
      return response
    },

    async fetchLinks({ commit, state }, opts) {
      commit('START_LINKS_FETCH')
      const id = opts.id || state.startup.id
      const response = await http.getAuthed(`/startups/${id}/links`)
      commit('SET_LINKS', { links: response.data })
      commit('STOP_LINKS_FETCH')
    },
    async addLink({ commit, state }, { name }) {
      const response = await http.postAuthed(
        `/startups/${state.startup.id}/add-link`,
        { name },
      )
      const newLinks = state.links.concat({ id: response.data.id, name })
      commit('SET_LINKS', { links: newLinks })
    },
    async removeLink({ commit, state }, { id }) {
      await http.postAuthed(
        `/startups/${state.startup.id}/links/${id}/remove`,
        { name },
      )
      commit('REMOVE_LINK', { id })
    },
    async restoreLink({ commit, state }, { id, name }) {
      commit('RESTORE_LINK', { id, name })
      await http.postAuthed(
        `/startups/${state.startup.id}/links/${id}/restore`,
        { name },
      )
    },

    async enableFeature({ commit, state }, { feature }) {
      commit('ENABLE_FEATURE', { feature })
      return await http.postAuthed(
        `/startups/${state.startup.id}/enable-feature`,
        { feature },
      )
    },
    async disableFeature({ commit, state }, { feature }) {
      commit('DISABLE_FEATURE', { feature })
      return await http.postAuthed(
        `/startups/${state.startup.id}/disable-feature`,
        { feature },
      )
    },
    rate,
  },
}

function getRoundIdx(state, round) {
  return state.startup.previous_rounds.indexOf(round)
}

async function fetchWithUrl({ commit, url, isAuthorized, silent = false }) {
  let method = http.get
  if (isAuthorized) {
    method = http.getAuthed
  }
  if (!silent) {
    commit('START_FETCH')
  }
  try {
    const response = await method(url)
    commit('CLEAN_API_ERRORS')
    commit('SET_STARTUP_DATA', response.data)
    return response
  } catch (error) {
    commit('API_ERROR', error.response.data)
    return
  } finally {
    if (!silent) {
      commit('STOP_FETCH')
    }
  }
}

async function rate({ commit, state }, { startupId, ratings }) {
  commit('startRate')
  const url = `/startups/${startupId}/rate`
  try {
    await http.postAuthed(url, { ratings })
    commit('setRatings', ratings)
  } finally {
    commit('stopRate')
  }
}

function swap(arr, idx1, idx2) {
  const newArr = arr.map((s) => s)
  const tmp = newArr[idx1]
  newArr[idx1] = newArr[idx2]
  newArr[idx2] = tmp
  return newArr
}

function makeNullsIfEmpty(data, fields) {
  const newData = { ...data }
  fields.forEach((field) => {
    if (!newData[field]) {
      delete newData[field]
    }
  })
  return newData
}
